/**
 * Повторная примерка забронированной сделки: создать, изменить целиком, отменить. В «Чатах» —
 * сверху вкладки «Договор» (планируют по договору, владелец 22.10.26), на карточках товаров —
 * отметки `VisitMark`; в расширении вкладки «Договор» нет — там `VisitBlock` в блоке сделки.
 * В форме товары идут по категориям каталога, под товаром — подшив и открытые задачи, у категорий
 * «Замер / Проверить подшив» — подсказка, если подшив товара с ними не сходится.
 * Форма раскрывается на месте, отмена — в два шага: CRM сама шлёт клиенту WhatsApp и о
 * повторке, и об отмене. Дизайн формы — Fable 5.1 (22.10.26). Спеки
 * `docs/specs/2026-10-21-deal-visit-design.md`, `docs/specs/2026-10-22-deal-visit-edit-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { DateTimePick } from "@/components/DateTimePick";
import { ProductTaskList, TaskPill } from "@/components/chat/ProductTasks";
import { ApiFailure } from "@/lib/api";
import type { ProductTask } from "@/lib/dealContract";
import {
  CATEGORY_TEXT,
  type VisitForm,
  type VisitPast,
  type VisitProduct,
  type VisitReserve,
  type VisitTask,
  busyWith,
  cancelVisit,
  createVisit,
  ddmm,
  editVisit,
  hasPodshiv,
  loadVisitForm,
  needsTailor,
  podshivHint,
  visitOf,
  visitWhen,
  weekendRush,
} from "@/lib/dealVisit";
import { btn } from "@/lib/panelBtn";

/** Что открыто: форма новой повторки, правка активной (`add` — товар, который отметить сразу). */
export type VisitOpen = { mode: "new" | "edit"; add?: number } | null;

const tinyLink = "inline-flex h-6 items-center whitespace-nowrap text-accent-text hover:underline";
const field =
  "w-full rounded-[var(--radius-ui)] border border-border bg-transparent px-2 py-1 leading-snug hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent";
const pill = "rounded-full px-1.5 py-px text-[11px] font-medium leading-[16px]";
const NO_TIME = "T00:00";
const PLANNED = 1;
const STATUS_TONE: Record<number, string> = {
  1: "bg-accent/10 text-accent-text",
  2: "border border-border text-fg-muted",
  3: "bg-ok-bg text-ok",
  4: "bg-warn-bg text-warn",
};
const STATUS_WORD: Record<number, string> = { 2: "отменена", 3: "прошла", 4: "просрочена" };

const many = (n: number) => `${n} ${n % 10 === 1 && n % 100 !== 11 ? "товар" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? "товара" : "товаров"}`;

/** Почему нельзя на это время — те же запреты, что у сервера; пусто — можно. */
function dateProblem(date: string, form: VisitForm): string | null {
  if (!date) return null;
  if (date.slice(10, 16) === NO_TIME) return "Укажите время повторки";
  if (date.slice(0, 16) <= form.deal.now) return "Это время уже прошло";
  if (form.deal.rent_start && date.slice(0, 10) > form.deal.rent_start)
    return `Повторка позже начала проката (${ddmm(form.deal.rent_start)})`;
  return null;
}

export const visitQuery = (dealId: number) => ({
  queryKey: ["deal-visit", dealId],
  queryFn: () => loadVisitForm(dealId),
  staleTime: 30_000,
});

function useRefresh(dealId: number, chatId: number | null) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["deal-visit", dealId] });
    void qc.invalidateQueries({ queryKey: ["deal-contract", dealId] });
    void qc.invalidateQueries({ queryKey: ["deal-card", dealId] });
    if (chatId) void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
  };
}

type Notice = { text: string; warnings: string[] };

/** Блок сделки расширения: та же строка и форма, состояние — своё. */
export function VisitBlock({ dealId, chatId }: { dealId: number; chatId: number | null }) {
  const q = useQuery(visitQuery(dealId));
  const [open, setOpen] = useState<VisitOpen>(null);
  // Не загрузилось (CRM недоступна, сервер старее фронта) — блока нет, остальная панель работает.
  if (q.isPending || q.isError) return null;
  return <VisitSection form={q.data} dealId={dealId} chatId={chatId} open={open} setOpen={setOpen} />;
}

/** Строка «Повторка на …» с действиями, форма и подтверждение отмены. */
export function VisitSection({
  form,
  dealId,
  chatId,
  open,
  setOpen,
}: {
  form: VisitForm;
  dealId: number;
  chatId: number | null;
  open: VisitOpen;
  setOpen: (v: VisitOpen) => void;
}) {
  const [notice, setNotice] = useState<Notice | null>(null);
  const [asking, setAsking] = useState(false);
  const a = form.active;
  const last = form.visits?.[0] ?? null;
  // Сделка не в брони и повторок не было — строки нет вовсе.
  if (!form.deal.booked && !a && !last) return null;
  const done = (n: Notice) => (setNotice(n), setOpen(null), setAsking(false));
  const start = (v: VisitOpen) => (setNotice(null), setAsking(false), setOpen(v));
  return (
    <div className="flex flex-col gap-1.5 text-[12.5px]" data-annot="повторка">
      {!open && !asking && <VisitLine form={form} last={last} onOpen={start} onCancel={() => (setNotice(null), setAsking(true))} />}
      {asking && a && <CancelAsk form={form} dealId={dealId} chatId={chatId} onClose={() => setAsking(false)} onDone={done} />}
      {open && (
        <VisitEditor
          key={`${open.mode}-${open.add ?? ""}-${a?.id ?? ""}`}
          form={form}
          dealId={dealId}
          chatId={chatId}
          open={open}
          onClose={() => setOpen(null)}
          onDone={done}
        />
      )}
      {notice && (
        <div role="status" className="rounded-[var(--radius-ui)] bg-ok-bg px-2 py-1 leading-snug text-ok">
          ✓ {notice.text}
        </div>
      )}
      {notice?.warnings.map((w) => <Warn key={w}>{w}</Warn>)}
    </div>
  );
}

function VisitLine({
  form,
  last,
  onOpen,
  onCancel,
}: {
  form: VisitForm;
  last: VisitPast | null;
  onOpen: (v: VisitOpen) => void;
  onCancel: () => void;
}) {
  const a = form.active;
  const can = form.deal.booked;
  if (a) {
    const text = (
      <>
        <span className="text-fg-muted">Повторка</span> <span className={`${pill} ${STATUS_TONE[PLANNED]}`}>запланирована</span>{" "}
        <b className="font-medium">{visitWhen(a.date)}</b>
        {a.category_name && ` · ${a.category_name}`}
        {a.serials.length > 0 && ` · ${many(a.serials.length)}`}
        {a.tailor && " · мастер"}
      </>
    );
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5" data-annot="строка повторки">
        {can ? (
          <button type="button" className="min-w-0 flex-1 text-left leading-snug hover:underline" title="Изменить повторку" onClick={() => onOpen({ mode: "edit" })}>
            {text}
          </button>
        ) : (
          <span className="min-w-0 flex-1 leading-snug">{text}</span>
        )}
        {can && (
          <button type="button" className={tinyLink} onClick={() => onOpen({ mode: "edit" })}>
            Изменить
          </button>
        )}
        <button type="button" className="inline-flex h-6 items-center text-fg-muted hover:text-err hover:underline" onClick={onCancel}>
          Отменить
        </button>
      </div>
    );
  }
  const cancelled = last && last.status === 2;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5" data-annot="строка повторки">
      <span className="min-w-0 flex-1 text-fg-muted">
        {cancelled ? `Повторка отменена (была на ${visitWhen(last.date)})` : "Повторной примерки нет"}
        {last && !cancelled && ` · последняя ${STATUS_WORD[last.status] ?? last.status_name ?? ""} ${last.date ? ddmm(last.date) : ""}`}
      </span>
      {can && (
        <button type="button" className={tinyLink} title="Запланировать повторную примерку — запишется в CRM" onClick={() => onOpen({ mode: "new" })}>
          {cancelled ? "+ Запланировать новую" : "+ Запланировать"}
        </button>
      )}
    </div>
  );
}

/** Отметка на карточке товара договора: последняя повторка товара; товара нет в активной —
 *  «+ в повторку» открывает правку с ним отмеченным. */
export function VisitMark({ form, serial, onOpen }: { form: VisitForm; serial: number | null; onOpen: (v: VisitOpen) => void }) {
  if (serial === null) return null;
  const v = visitOf(form, serial);
  const a = form.active;
  const can = form.deal.booked;
  const inDeal = form.products.some((p) => p.serial === serial && p.in_deal);
  const mark = v && (
    <span className={`${pill} ${STATUS_TONE[v.status] ?? STATUS_TONE[2]}`} title={[v.category_name, v.status_name].filter(Boolean).join(" · ")}>
      {v.status === PLANNED ? `повторка ${visitWhen(v.date)}` : `повторка ${STATUS_WORD[v.status] ?? v.status_name ?? ""} ${v.date ? ddmm(v.date) : ""}`}
    </span>
  );
  return (
    <>
      {mark && v?.status === PLANNED && can ? (
        <button type="button" className="hover:opacity-80" title="Изменить повторку" onClick={() => onOpen({ mode: "edit" })}>
          {mark}
        </button>
      ) : (
        mark
      )}
      {a && can && inDeal && !a.serials.includes(serial) && (
        <button type="button" className="text-[11.5px] text-accent-text hover:underline" onClick={() => onOpen({ mode: "edit", add: serial })}>
          + в повторку
        </button>
      )}
    </>
  );
}

/** Окно задачи товара: плашка, автор, текст и фото — тот же список, что во вкладке «Договор». */
function TaskWindow({ p, task, onClose }: { p: VisitProduct; task: VisitTask; onClose: () => void }) {
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => close.current?.focus(), []);
  const t: ProductTask = { ...task, row: 0, deal: null, previews: task.previews ?? [], images: task.images ?? [] };
  return createPortal(
    <div
      className="fixed inset-0 z-[90] grid place-items-center bg-black/30 p-4"
      onClick={onClose}
      // Escape закрывает только окно, не форму под ним (события портала всплывают по React).
      onKeyDown={(e) => e.key === "Escape" && (e.stopPropagation(), onClose())}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Задача «${task.name}» — ${p.name}`}
        className="flex w-full max-w-sm flex-col gap-2 rounded-[var(--radius-ui)] border border-border bg-bg p-3 text-[12.5px] text-fg shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-annot="окно задачи товара"
      >
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 truncate font-medium" title={p.name}>
            {p.name}
          </div>
          <button ref={close} type="button" className="grid size-6 place-items-center rounded text-fg-muted hover:bg-bg-hover hover:text-fg" aria-label="Закрыть" title="Закрыть (Esc)" onClick={onClose}>
            <X size={14} aria-hidden />
          </button>
        </div>
        <ProductTaskList tasks={[t]} />
      </div>
    </div>,
    document.body,
  );
}

/** «Платье · 416601» → вид и серия отдельно: подпись под фото в две строки. */
function nameParts(name: string): [string, string] {
  const i = name.lastIndexOf(" · ");
  return i < 0 ? [name, ""] : [name.slice(0, i), name.slice(i + 3)];
}

/** Карточка товара в форме: фото с отметкой, вид и серия, задачи плашками (клик — окно задачи),
 *  подшив строки договора, чужие брони на это время (владелец 22.10.26: строку с маленьким фото
 *  трудно осмотреть). */
function ProductCard({ p, checked, hit, onToggle }: { p: VisitProduct; checked: boolean; hit: VisitReserve[]; onToggle: () => void }) {
  const [task, setTask] = useState<VisitTask | null>(null);
  const gone = !p.in_deal;
  const [kind, serial] = nameParts(p.name);
  const tasks = p.tasks ?? [];
  const ring = hit.length ? "border-warn" : checked ? "border-accent ring-1 ring-accent" : "border-border";
  return (
    <div className={`flex min-w-0 flex-col overflow-hidden rounded-[var(--radius-ui)] border ${ring}`} data-annot="товар повторки">
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={p.name}
        disabled={gone && !checked}
        className={`relative block aspect-[3/4] w-full bg-bg-subtle ${gone && !checked ? "opacity-50" : "hover:opacity-90"}`}
        onClick={onToggle}
      >
        {p.photo && <img src={p.photo} alt="" className="absolute inset-0 size-full object-cover" loading="lazy" />}
        <span
          className={`absolute left-1 top-1 grid size-5 place-items-center rounded-full border-2 shadow ${checked ? "border-accent bg-accent text-white" : "border-white bg-black/20"}`}
          aria-hidden
        >
          {checked && <Check size={12} strokeWidth={3} />}
        </span>
        {gone && <span className="absolute inset-x-0 bottom-0 bg-black/60 py-0.5 text-center text-[10.5px] text-white">нет в сделке</span>}
      </button>
      <div className="flex min-w-0 flex-col gap-1 p-1 text-[11px] leading-tight">
        <div className="min-w-0">
          <div className="truncate font-medium" title={p.name}>
            {kind}
          </div>
          {serial && <div className="text-fg-muted">{serial}</div>}
        </div>
        {(tasks.length > 0 || p.contract_tailor) && (
          <div className="flex flex-wrap gap-1">
            {tasks.map((t) => (
              <button key={t.id} type="button" className="hover:opacity-80" title={`${[t.status_name, t.text].filter(Boolean).join(" — ")}\nоткрыть задачу`} onClick={() => setTask(t)}>
                <TaskPill name={t.name} color={t.color} status={null} />
              </button>
            ))}
            {p.contract_tailor && (
              <span className={`${pill} border border-border text-fg-muted`} title="Подшив в строке договора">
                {p.contract_tailor}
              </span>
            )}
          </div>
        )}
        {hit.map((r) => (
          <div key={r.deal} className="rounded bg-warn-bg px-1 py-0.5 text-warn" title={`В другой брони: сделка ${r.deal}`}>
            бронь {r.deal}, {ddmm(r.start)}–{ddmm(r.end)}
          </div>
        ))}
      </div>
      {task && <TaskWindow p={p} task={task} onClose={() => setTask(null)} />}
    </div>
  );
}

/** Первый шаг формы — зачем повторка, как цели страницы брони my: карточки с описанием.
 *  «Проверить подшив» закрыт, пока ни у одного товара нет задачи на подшив. */
function CategoryStep({ form, current, was, onPick }: { form: VisitForm; current: number | null; was: number | null; onPick: (id: number) => void }) {
  const anyPodshiv = form.products.some((p) => p.in_deal && hasPodshiv(p));
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => first.current?.focus(), []);
  return (
    <div className="flex flex-col gap-1" role="radiogroup" aria-label="Зачем повторка" data-visit-cat>
      <div className="text-[11.5px] text-fg-muted">зачем повторка</div>
      {form.categories.map((c, i) => {
        const off = c.podshiv === "check" && !anyPodshiv && c.id !== was;
        const on = current === c.id;
        return (
          <button
            key={c.id}
            ref={i === 0 ? first : undefined}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={off}
            className={`flex items-start gap-2 rounded-[var(--radius-ui)] border px-2 py-1.5 text-left disabled:cursor-not-allowed disabled:opacity-60 ${
              on ? "border-accent bg-accent/5" : "border-border enabled:hover:border-border-strong enabled:hover:bg-bg-hover"
            }`}
            onClick={() => onPick(c.id)}
          >
            <span className={`mt-0.5 grid size-4 flex-none place-items-center rounded-full border-2 ${on ? "border-accent" : "border-border-strong"}`} aria-hidden>
              {on && <span className="size-2 rounded-full bg-accent" />}
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="flex flex-wrap items-center gap-1 font-medium">
                {c.name}
                {needsTailor(c) && <span className={`${pill} bg-accent/10 text-accent-text`}>с мастером</span>}
                {was === c.id && <span className={`${pill} border border-border text-fg-muted`}>сейчас</span>}
              </span>
              <span className="text-[11.5px] leading-snug text-fg-muted">{CATEGORY_TEXT[c.id] ?? c.hint ?? ""}</span>
              {off && (
                <span className="text-[11.5px] leading-snug text-warn">
                  Нет задачи на подшив ни у одного товара — проверять нечего. Сначала «Замер подшива» или задача «Подшив» товару.
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Warn({ children }: { children: React.ReactNode }) {
  return <div className="rounded-[var(--radius-ui)] bg-warn-bg px-2 py-1 leading-snug text-warn">{children}</div>;
}

/** Точка у подписи поля, которое в правке изменилось. */
function Dot({ on }: { on: boolean }) {
  return on ? <span className="mr-1 inline-block size-1.5 rounded-full bg-accent align-middle" aria-label="изменено" /> : null;
}

const stale = (e: unknown) => e instanceof ApiFailure && (e.status === 404 || e.status === 409);

function VisitEditor({
  form,
  dealId,
  chatId,
  open,
  onClose,
  onDone,
}: {
  form: VisitForm;
  dealId: number;
  chatId: number | null;
  open: NonNullable<VisitOpen>;
  onClose: () => void;
  onDone: (n: Notice) => void;
}) {
  const refresh = useRefresh(dealId, chatId);
  const a = open.mode === "edit" ? form.active : null;
  const cats = form.categories;
  const catOf = (id: number | null | undefined) => cats.find((c) => c.id === id);
  // Товары: сделки + уже стоящие в повторке, хотя их из сделки убрали (их можно только снять).
  const products = form.products.filter((p) => p.in_deal || a?.serials.includes(p.serial));
  const initSerials = a
    ? [...new Set([...a.serials, ...(open.add ? [open.add] : [])])]
    : products.length === 1
      ? [products[0]!.serial]
      : [];
  const [serials, setSerials] = useState<number[]>(initSerials);
  const [category, setCategory] = useState<number | null>(a?.category ?? null);
  // Сначала — зачем повторка (новая); в правке категория уже есть — сразу вещи и время.
  const [step, setStep] = useState<"cat" | "rest">(a ? "rest" : "cat");
  const [date, setDate] = useState(a?.date ?? "");
  const [comment, setComment] = useState(a?.comment ?? "");
  const cat = catOf(category);
  // Мастер — по категории, галочки нет (владелец 22.10.26); старую повторку с мастером,
  // поставленным руками, при той же категории не трогаем.
  const tailor = needsTailor(cat) || (!!a && category === a.category && a.tailor);
  const pick = (id: number) => {
    // «Проверить подшив» и ничего не отмечено — сразу вещи с задачей на подшив.
    if (catOf(id)?.podshiv === "check" && serials.length === 0) setSerials(products.filter((p) => p.in_deal && hasPodshiv(p)).map((p) => p.serial));
    setCategory(id);
    setStep("rest");
  };
  const body = { category: category!, date, serials, tailor, comment };
  const save = useMutation({
    mutationFn: () => (a ? editVisit(dealId, a.id, body, chatId ?? undefined) : createVisit(dealId, body, chatId ?? undefined)),
    onSuccess: (r) => {
      refresh();
      onDone({
        text: a ? `Изменения сохранены — повторка ${visitWhen(r.date)}` : `Повторка ${visitWhen(r.date)} записана в CRM — клиенту уйдёт WhatsApp`,
        warnings: r.warnings,
      });
    },
  });

  // Изменения правки — на лету, по ним же точки у полей и строка «Изменится».
  const added = a ? serials.filter((s) => !a.serials.includes(s)) : [];
  const removed = a ? a.serials.filter((s) => !serials.includes(s)) : [];
  const ch = a && {
    date: date !== a.date,
    serials: added.length > 0 || removed.length > 0,
    category: category !== a.category,
    tailor: tailor !== a.tailor,
    comment: comment.trim() !== (a.comment ?? "").trim(),
  };
  const changes = ch
    ? [
        ch.date && (
          <span key="d">
            <b className="font-medium">время</b> (было {visitWhen(a.date)})
          </span>
        ),
        ch.serials && (
          <span key="s">
            <b className="font-medium">товары</b> ({[added.length && `+${added.length}`, removed.length && `−${removed.length}`].filter(Boolean).join(" ")})
          </span>
        ),
        ch.category && (
          <b key="c" className="font-medium">
            категория
          </b>
        ),
        ch.tailor && (
          <b key="t" className="font-medium">
            мастер
          </b>
        ),
        ch.comment && (
          <b key="m" className="font-medium">
            комментарий
          </b>
        ),
      ].filter(Boolean)
    : [];

  // Время проверяем, только если его меняли (как сервер): правка товаров повторки, которая вот-вот
  // начнётся, возможна.
  const problem = a && !ch?.date ? null : dateProblem(date, form);
  const needProduct = !!cat?.need_product && serials.length === 0;
  // «Проверить подшив» без вещи с задачей на подшив — проверять нечего.
  const needPodshiv = cat?.podshiv === "check" && serials.length > 0 && !products.some((p) => serials.includes(p.serial) && hasPodshiv(p));
  const missing = [
    !cat && "выберите, зачем повторка",
    (!date || date.slice(10, 16) === NO_TIME) && !problem && "укажите день и время",
    needProduct && "отметьте товар",
    needPodshiv && "отметьте вещь с задачей на подшив",
  ].filter(Boolean) as string[];
  const nothing = !!a && changes.length === 0;
  const ready = missing.length === 0 && !problem && !nothing && !save.isPending;
  // Подшив отмеченных товаров против категории «Замер» / «Проверить подшив».
  const hints = products.filter((p) => serials.includes(p.serial)).map((p) => podshivHint(cat, p)).filter(Boolean) as string[];
  const toggle = (s: number) => setSerials((v) => (v.includes(s) ? v.filter((x) => x !== s) : [...v, s]));

  // Автофокус после выбора категории: правка и один товар (он уже отмечен) — время (перенос —
  // самое частое), иначе — первая вещь. На шаге категорий фокус ставит сам шаг.
  const box = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const el = box.current;
    if (!el || step !== "rest") return;
    const sel = a || products.length === 1 ? "[data-visit-date] button" : "[data-visit-products] button[role=checkbox]";
    el.querySelector<HTMLElement>(sel)?.focus();
  }, [step]);

  return (
    <form
      ref={box}
      className="flex flex-col gap-2 rounded-[var(--radius-ui)] border border-border p-2"
      onSubmit={(e) => (e.preventDefault(), ready && save.mutate())}
      // Escape закрывает форму без вопросов; в выборе даты Escape закрывает только его.
      onKeyDown={(e) => {
        if (e.key === "Escape" && !(e.target as HTMLElement).closest("[data-visit-date]")) onClose();
      }}
      data-annot={a ? "правка повторки" : "новая повторка"}
    >
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1 text-[12.5px] font-medium">{a ? `Изменить повторку ${visitWhen(a.date)}` : "Повторная примерка"}</div>
        <button type="button" className="grid size-6 place-items-center rounded text-fg-muted hover:bg-bg-hover hover:text-fg" aria-label="Закрыть" title="Закрыть (Esc)" onClick={onClose}>
          <X size={14} aria-hidden />
        </button>
      </div>
      {step === "cat" ? (
        <CategoryStep form={form} current={category} was={a?.category ?? null} onPick={pick} />
      ) : (
        <fieldset disabled={save.isPending} className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 leading-snug" data-annot="зачем повторка">
            <span className="min-w-0 flex-1">
              <Dot on={!!ch?.category || !!ch?.tailor} />
              <span className="text-fg-muted">Зачем:</span> <b className="font-medium">{cat?.name ?? "—"}</b>
              {tailor && <span className="text-fg-muted"> · с мастером</span>}
            </span>
            <button type="button" className={tinyLink} onClick={() => setStep("cat")}>
              Сменить
            </button>
          </div>
          {hints.map((h) => (
            <Warn key={h}>{h}</Warn>
          ))}
          <div className="flex flex-col gap-1" data-visit-products>
            <div className="text-[11.5px] text-fg-muted">
              <Dot on={!!ch?.serials} />
              вещи{cat && !cat.need_product ? " — по желанию" : ""}
            </div>
            {products.length === 0 && <div className="text-fg-muted">в сделке нет товаров</div>}
            <div className="grid grid-cols-3 gap-1.5">
              {products.map((p) => {
                const checked = serials.includes(p.serial);
                return <ProductCard key={p.serial} p={p} checked={checked} hit={date && checked ? busyWith(p, date) : []} onToggle={() => toggle(p.serial)} />;
              })}
            </div>
          </div>
          <div data-visit-date>
            <DateTimePick label={ch?.date ? "день и время · изменено" : "день и время"} value={date} onChange={setDate} placeholder="выбрать день и время" compact />
            <div className="mt-0.5 text-[11.5px] text-fg-muted">
              прокат с {ddmm(form.deal.rent_start)} · время салона{form.deal.city ? ` (${form.deal.city})` : ""}
            </div>
          </div>
          <div>
            <div className="mb-0.5 text-[11.5px] text-fg-muted">
              <Dot on={!!ch?.comment} />
              комментарий
            </div>
            <input
              className={field}
              maxLength={480}
              placeholder="необязательно"
              aria-label="Комментарий к повторке"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        </fieldset>
      )}
      {step === "rest" && problem && <div className="text-err">{problem}</div>}
      {step === "rest" && weekendRush(date) && (!a || ch?.date) && <Warn>Выходные 12–17 — пик примерок, лучше предложить другое</Warn>}
      {step === "rest" && a && (
        <div className="leading-snug text-fg-muted" data-annot="что изменится">
          {nothing ? (
            "Ничего не изменено"
          ) : (
            <>
              Изменится:{" "}
              {changes.map((c, i) => (
                <span key={i}>
                  {i > 0 && " · "}
                  {c}
                </span>
              ))}
            </>
          )}
        </div>
      )}
      {save.isError && <div className="text-err">{save.error.message}</div>}
      <div className="flex flex-wrap items-center gap-2">
        {step === "cat" ? null : save.isError && stale(save.error) ? (
          <button type="button" className={btn.outline} onClick={() => (refresh(), onClose())}>
            Обновить
          </button>
        ) : (
          <button type="submit" className={btn.primary} disabled={!ready}>
            {save.isPending ? "Записываю в CRM…" : a ? "Сохранить изменения" : "Запланировать"}
          </button>
        )}
        <button type="button" className={btn.quiet} onClick={onClose} disabled={save.isPending}>
          Закрыть
        </button>
      </div>
      {step === "rest" && missing.length > 0 && !problem && (
        <div className="text-[11.5px] text-fg-muted">
          Чтобы {a ? "сохранить" : "запланировать"}: {missing.join(" · ")}
        </div>
      )}
    </form>
  );
}

function CancelAsk({
  form,
  dealId,
  chatId,
  onClose,
  onDone,
}: {
  form: VisitForm;
  dealId: number;
  chatId: number | null;
  onClose: () => void;
  onDone: (n: Notice) => void;
}) {
  const refresh = useRefresh(dealId, chatId);
  const a = form.active!;
  const keep = useRef<HTMLButtonElement>(null);
  useEffect(() => keep.current?.focus(), []);
  const run = useMutation({
    mutationFn: () => cancelVisit(dealId, a.id, chatId ?? undefined),
    onSuccess: (r) => {
      refresh();
      onDone({ text: `Повторка ${visitWhen(r.date)} отменена`, warnings: [] });
    },
  });
  return (
    <div
      role="alertdialog"
      aria-label="Отменить повторку"
      className="flex flex-col gap-2 rounded-[var(--radius-ui)] border border-err bg-err-bg p-2"
      onKeyDown={(e) => e.key === "Escape" && !run.isPending && onClose()}
      data-annot="отмена повторки"
    >
      <div className="font-medium">Отменить повторку {visitWhen(a.date)}?</div>
      <div className="leading-snug">Клиенту уйдёт WhatsApp «повторка отменена» — его шлёт CRM, отозвать нельзя.</div>
      {run.isError && <div className="text-err">{run.error.message}</div>}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="inline-flex h-9 items-center rounded-[var(--radius-ui)] bg-err px-3 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50"
          disabled={run.isPending}
          onClick={() => run.mutate()}
        >
          {run.isPending ? "Отменяю…" : "Да, отменить повторку"}
        </button>
        <button ref={keep} type="button" className={btn.quiet} disabled={run.isPending} onClick={onClose}>
          Оставить
        </button>
      </div>
    </div>
  );
}
